import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import { config } from "../lib/config";
import { timeZoneOptions, currentTimeIn } from "../shared/timezones";
import type { CrewPurpose } from "../shared/checkin-prompts";
import { HostWizard } from "./HostWizard";
import { describeWindow } from "../shared/copy";
import { HostHub, type HostSection } from "./HostHub";
import {
  defaultSeasonSettings, seasonSettingsFromRow, configurePayload, seasonSettingsProblems,
  type SeasonSettings,
} from "../shared/season-settings";
import { BlobField } from "../components/BlobField";

// Four sections rather than one endless page. Named for the question a host is
// actually asking when they open the app, not for the tables underneath.
const HOST_SECTIONS: { key: HostSection; label: string }[] = [
  { key: "season", label: "Season" },
  { key: "crew", label: "Crew" },
  { key: "drops", label: "Drops" },
  { key: "alerts", label: "Alerts" },
];
import { endsOnFor, DEFAULT_DROP_WINDOW, DEFAULT_DROP_DAYS } from "../shared/season-plan";

// Today's date in the crew's own zone. A host in Los Angeles setting up at 9pm
// should not be offered tomorrow as the earliest start.
function todayIn(timezone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

// Commissioner dashboard. Operationally powerful, competitively BLIND: sees
// inventory COUNTS and notification readiness, never another member's question
// content, transcripts, or future selections. No exact future drop time is shown.
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PLAY_WINDOW_MINUTES = [10, 25, 60, 120, 240, 480, 720, 1440]; // how long a drop stays open
const ANSWER_TIMERS = [10, 15, 20, 30]; // seconds per question

function DayPicker({ selected, onToggle }: { selected: number[]; onToggle: (d: number) => void }) {
  return (
    <div className="row" style={{ flexWrap: "wrap" }}>
      {WEEKDAYS.map((w, i) => {
        const sel = selected.includes(i);
        return (
          <button
            key={w}
            onClick={() => onToggle(i)}
            style={{
              minWidth: 46,
              padding: "10px 12px",
              borderRadius: 10,
              fontWeight: 800,
              cursor: "pointer",
              border: sel ? "2px solid var(--accent)" : "1px solid #3a4050",
              background: sel ? "var(--accent)" : "var(--surface-2)",
              color: sel ? "#1a1400" : "var(--text)",
            }}
          >
            {w}
          </button>
        );
      })}
    </div>
  );
}

export function Commissioner() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<any>(null);
  const [league, setLeague] = useState<any>(null);
  const [invite, setInvite] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleMsg, setRescheduleMsg] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState(false);

  // The season settings being edited. One object, the same shape the setup
  // wizard uses, because a save writes all of it: holding half of it here is
  // what let one screen reset the other's fields.
  const [cfg, setCfg] = useState<SeasonSettings>(defaultSeasonSettings);
  const edit = (patch: Partial<SeasonSettings>) => setCfg((c) => ({ ...c, ...patch }));
  const [problems, setProblems] = useState<string[]>([]);
  const [sect, setSect] = useState<HostSection>("season");
  const [crewLink, setCrewLink] = useState<string | null>(null);
  const [crewLinkOpen, setCrewLinkOpen] = useState(true);

  async function loadCrewLink(rotate: boolean) {
    const r = await api<{ url: string; open: boolean }>(`/api/league/admin`, {
      body: { op: "crew-link", leagueId, rotate },
    });
    setCrewLink(r.url);
    setCrewLinkOpen(r.open);
    if (rotate) setActionMsg("New link made. The old one no longer works.");
  }

  async function setLinkOpen(open: boolean) {
    const r = await api<{ url: string; open: boolean }>(`/api/league/admin`, {
      body: { op: "crew-link", leagueId, open },
    });
    setCrewLinkOpen(r.open);
    setActionMsg(open ? "Open. People can join again." : "Closed. Nobody new can join for now.");
  }

  // Editable league basics (no money/stakes).
  const [lname, setLname] = useState("");
  const [ltz, setLtz] = useState("");

  async function load() {
    setLoadErr("");
    const { data: lg } = await supabase.from("leagues").select("*").eq("id", leagueId).single();
    setLeague(lg);
    if (lg) { setLname(lg.name ?? ""); setLtz(lg.timezone ?? ""); }
    const s = await api(`/api/league/admin`, { body: { op: "status", leagueId } });
    // Only reads the existing one. A link is created when a host asks for it,
    // never as a side effect of opening a page.
    if (lg?.crew_link_token) {
      setCrewLink(`${config.appUrl.replace(/\/$/, "")}/join/${lg.crew_link_token}`);
      setCrewLinkOpen(lg.crew_link_open !== false);
    }
    setStatus(s);
    if ((s as any).season) setCfg(seasonSettingsFromRow((s as any).season));
  }
  useEffect(() => { load().catch((e: any) => setLoadErr(e?.message ?? "Could not load the host page.")); }, [leagueId]);

  // Every commissioner action goes through here. Three of them used to call the
  // API with no try/catch and no busy state, so a failure rejected an un-awaited
  // promise and the button simply did nothing. This is the same treatment `op`
  // already had, applied to the rest of them.
  async function run(working: string, fn: () => Promise<string>) {
    setBusy(true);
    setActionMsg(working);
    try {
      const msg = await fn();
      await load();
      setActionMsg(msg);
    } catch (e: any) {
      setActionMsg(e?.message ?? "That didn't work. Try it again.");
    } finally {
      setBusy(false);
    }
  }

  async function saveLeague() {
    await run("Saving...", async () => {
      await api(`/api/league/admin`, { body: { op: "update", leagueId, name: lname, timezone: ltz } });
      return "Crew settings saved.";
    });
  }

  async function saveConfig() {
    // A refusal has to be visible. These checks used to write a line into a
    // banner at the top of a long page, so from down here by the button the
    // save looked like it had simply done nothing.
    const found = seasonSettingsProblems(cfg);
    setProblems(found);
    if (found.length > 0) {
      setActionMsg(found[0]);
      return;
    }
    await run("Saving...", async () => {
      const r = await api<{ note?: string }>(`/api/league/admin`, {
        body: { op: "configure", leagueId, ...configurePayload(cfg) },
      });
      await load();
      return r?.note ?? "Settings saved.";
    });
  }

  async function activate() {
    await run("Setting the schedule...", async () => {
      const r = await api<{ note?: string }>(`/api/league/admin`, { body: { op: "activate", leagueId } });
      return r?.note ?? "Season is live.";
    });
  }

  // Rebuilds only the drops that haven't opened yet. Already-played drops are
  // untouched, but the upcoming secret times all change — so confirm first.
  async function reschedule() {
    const ok = window.confirm(
      "Rebuild the remaining schedule from your current settings?\n\n" +
      "Drops that already happened stay exactly as they are. Every upcoming drop gets a new secret time."
    );
    if (!ok) return;
    setRescheduling(true);
    setRescheduleMsg("");
    try {
      // The server builds this message: it is the only side that knows how many
      // times landed in the past and which day the next drop falls on.
      const r = await api<{ upcoming: number; kept: number; note?: string }>(`/api/league/admin`, { body: { op: "reschedule", leagueId } });
      setRescheduleMsg(r.note ?? `Done. ${r.upcoming} drop${r.upcoming === 1 ? "" : "s"} scheduled ahead.`);
      await load();
    } catch (e: any) {
      setRescheduleMsg(e?.message ?? "Could not rebuild the schedule.");
    } finally {
      setRescheduling(false);
    }
  }
  // Binding the invite to an email up front is what makes joining browser-proof:
  // the server can then match the invite to whoever signs in with that address,
  // with no token to carry between Safari and the installed app.
  async function makeInvite() {
    const who = inviteEmail.trim().toLowerCase();
    await run("Adding...", async () => {
      const r = await api<{ inviteUrl: string }>(`/api/league/admin`, {
        body: { op: "invite", leagueId, email: who },
      });
      setInvite(r.inviteUrl);
      setInviteEmail("");
      return `${who} is on the list. Now send them the app.`;
    });
  }
  // Every commissioner action used to swallow its result. A failed call rejected
  // an un-awaited promise and the screen simply did nothing, which is why a
  // "send ping" with no live drop looked like a dead button.
  async function op(name: string, extra: any = {}, successMsg?: string) {
    await run("Working...", async () => {
      const r = await api<any>(`/api/league/admin`, { body: { op: name, leagueId, ...extra } });
      // The server note carries the delivery detail; a canned message would hide it.
      return r?.note ?? successMsg ?? "Done.";
    });
  }

  if (loadErr) {
    return (
      <div className="app">
        <h1>Host</h1>
        <p className="error">{loadErr}</p>
        <button className="btn primary" onClick={() => load().catch((e: any) => setLoadErr(e?.message ?? "Still failing."))}>
          Try again
        </button>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Back to the crew</button>
      </div>
    );
  }
  if (!status || !league) return <div className="app"><div className="loading">Loading…</div></div>;
  const season = status.season;

  // Two modes on one page. A season that has not started yet gets the guided
  // wizard, because the on-ramp is where a first-time host actually needs help.
  // Once it is running, the same host needs a calm place to watch it, not a
  // sequence of questions they already answered.
  if (!season || season.status === "draft") {
    return (
      <HostWizard
        leagueId={leagueId!}
        status={status}
        onReload={load}
        initial={{
          // The wizard edits the same settings object this page does, so there
          // is nothing to translate and nothing to forget. It used to rebuild
          // the whole shape by hand from a mixture of local state and the raw
          // season row, which is how the two screens drifted apart.
          ...cfg,
          crewName: league.name ?? "",
          timezone: league.timezone ?? "America/New_York",
          startsOn: cfg.startsOn || todayIn(league.timezone ?? "America/New_York"),
          endsOn: cfg.endsOn || endsOnFor(cfg.startsOn || todayIn(league.timezone ?? "America/New_York"), 14),
          activeWeekdays: cfg.activeWeekdays.length ? cfg.activeWeekdays : DEFAULT_DROP_DAYS,
          windowStart: season ? cfg.windowStart : DEFAULT_DROP_WINDOW.start,
          windowEnd: season ? cfg.windowEnd : DEFAULT_DROP_WINDOW.end,
        }}
      />
    );
  }
  // Removed members keep their rows for history but must not skew the counts the
  // commissioner acts on, or a removed player reads as someone still owing an update.
  const activeMembers = (status.members ?? []).filter((m: any) => m.inviteStatus !== "removed");
  const appHost = (config.appUrl || window.location.origin).replace(/^https?:\/\//, "").replace(/\/$/, "");
  // Anyone besides the commissioner, either joined or still to sign in.
  const peopleAdded =
    activeMembers.filter((m: any) => m.role !== "commissioner").length > 0 ||
    (status.pendingInvites ?? []).length > 0;
  const lowInventory = activeMembers.filter((m: any) => m.readyCount === 0);
  const totalReady = activeMembers.reduce((sum: number, m: any) => sum + (m.readyCount ?? 0), 0);

  return (
    <div className="app">
      {/* The host page is reached from inside a tab rather than being one, so it
          needs its own light. Without this it was the one screen still sitting
          on flat paper. */}
      <BlobField count={6} />
      <div className="header">
        <h1>Host</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
      </div>

      {actionMsg && (
        <div className="banner">
          <span>{actionMsg}</span>
          <button className="link" onClick={() => setActionMsg("")}>dismiss</button>
        </div>
      )}

      <HostHub
        league={league}
        status={status}
        onOpenSection={setSect}
        onSeeFinale={() => navigate(`/league/${leagueId}/finale`)}
      />

      {/* Four sections instead of one page that never ends.
          The Season 0 host page put every control on a single scroll, so
          finding anything meant swiping past everything else. These are the
          same controls, grouped by the question being asked: what are the
          rules, who is in, what has dropped, is everyone reachable. */}
      <div className="seg hostseg" role="tablist" aria-label="Host sections">
        {HOST_SECTIONS.map((s) => (
          <button
            key={s.key}
            role="tab"
            aria-selected={sect === s.key}
            className={`seg-btn${sect === s.key ? " current" : ""}`}
            onClick={() => setSect(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {sect === "season" && (
        <>
      {/* A brand new commissioner used to land on a wall of settings with nothing
          telling them what to do first, in what order, or when they were done.
          This disappears once the season is running. */}
      {season?.status !== "active" && season?.status !== "complete" && (
        <div className="card live">
          <span className="tag">Getting your crew going</span>
          <ul className="checklist">
            <li className={season ? "done" : "todo"}>
              <span className="mark">{season ? "✓" : "○"}</span>
              <span>Set your season: dates, drop days, times</span>
            </li>
            <li className={peopleAdded ? "done" : "todo"}>
              <span className="mark">{peopleAdded ? "✓" : "○"}</span>
              <span>Add your people and text them the app</span>
            </li>
            <li className={totalReady > 0 ? "done" : "todo"}>
              <span className="mark">{totalReady > 0 ? "✓" : "○"}</span>
              <span>Everybody checks in, so there are questions to play</span>
            </li>
            <li className="todo">
              <span className="mark">○</span>
              <span>Start the season</span>
            </li>
          </ul>
          <p className="muted">
            Nothing drops until you start the season. A drop with no approved questions gets skipped, so
            get a few check ins in first.
          </p>
        </div>
      )}

      {/* Season setup */}
      <div className="card">
        <span className="tag">What this crew is for</span>
        <p className="muted">
          This changes what your people get asked at check-in. Both go the same depth,
          they just start from different places.
        </p>
        <div className="grid" style={{ gap: 8 }}>
          {([
            { key: "keeping_up", title: "Keeping up",
              blurb: "People who already know each other and want to stay current." },
            { key: "getting_to_know", title: "Getting to know each other",
              blurb: "A group that's still new to each other." },
          ] as { key: CrewPurpose; title: string; blurb: string }[]).map((o) => (
            <button
              key={o.key}
              className={`choice${cfg.purpose === o.key ? " current" : ""}`}
              aria-pressed={cfg.purpose === o.key}
              onClick={() => edit({ purpose: o.key })}
            >
              <span className="choice-title">{o.title}</span>
              <span className="choice-blurb">{o.blurb}</span>
            </button>
          ))}
        </div>

        <span className="tag">Season length</span>
        <label className="muted">Start date</label>
        <input className="input" type="date" value={cfg.startsOn} onChange={(e) => edit({ startsOn: e.target.value })} />
        <label className="muted">End date</label>
        <input className="input" type="date" value={cfg.endsOn} onChange={(e) => edit({ endsOn: e.target.value })} />

        <label className="muted">Check-in cadence <span style={{ opacity: 0.7 }}>(how often members are prompted for new life updates)</span></label>
        <select className="input" value={cfg.checkInCadence} onChange={(e) => edit({ checkInCadence: e.target.value })}>
          <option value="once">Once at the beginning</option>
          <option value="weekly">Weekly</option>
          <option value="twice_weekly">Twice per week</option>
        </select>

        <label className="muted">Drop days <span style={{ opacity: 0.7 }}>(which days a drop can happen, pick any)</span></label>
        <DayPicker selected={cfg.activeWeekdays} onToggle={(d) => edit({
          activeWeekdays: cfg.activeWeekdays.includes(d)
            ? cfg.activeWeekdays.filter((x) => x !== d)
            : [...cfg.activeWeekdays, d].sort(),
        })} />

        <label className="muted">Drops a day</label>
        <div className="row" style={{ flexWrap: "wrap" }}>
          {[1, 2, 3, 4].map((n) => (
            <button key={n} className={`chip ${cfg.dropsPerDay === n ? "on" : ""}`} onClick={() => edit({ dropsPerDay: n })}>
              {n}
            </button>
          ))}
        </div>

        {/* Surprise or set times. This had no control here at all, so a host who
            picked exact times in setup lost them the first time they saved
            anything on this page. */}
        <label className="muted">When drops land</label>
        <div className="seg" role="group" aria-label="Drop timing">
          <button
            className={cfg.timingStyle === "surprise" ? "on" : ""}
            onClick={() => edit({ timingStyle: "surprise" })}
          >
            Surprise times
          </button>
          <button
            className={cfg.timingStyle === "exact" ? "on" : ""}
            onClick={() => edit({ timingStyle: "exact" })}
          >
            Times I pick
          </button>
        </div>

        {cfg.timingStyle === "surprise" ? (
          <>
            <label className="muted">Drop time range <span style={{ opacity: 0.7 }}>(a drop arrives at a random time in this window)</span></label>
            <div className="row">
              <input className="input" type="time" value={cfg.windowStart} onChange={(e) => edit({ windowStart: e.target.value })} />
              <input className="input" type="time" value={cfg.windowEnd} onChange={(e) => edit({ windowEnd: e.target.value })} />
            </div>
            <p className="muted">
              The drop lands at one random minute inside this range, not at the start of it. Saving a change
              rolls a new time for every remaining day, and any time that has already passed today is skipped,
              so changing this <b>during or after</b> today&apos;s range usually means no drop today.
            </p>
          </>
        ) : (
          <>
            <label className="muted">Drop times <span style={{ opacity: 0.7 }}>(one for each drop in a day)</span></label>
            {Array.from({ length: cfg.dropsPerDay }, (_, i) => (
              <input
                key={i}
                className="input"
                type="time"
                value={cfg.exactTimes[i] ?? ""}
                onChange={(e) => {
                  const next = [...cfg.exactTimes];
                  next[i] = e.target.value;
                  edit({ exactTimes: next.slice(0, cfg.dropsPerDay) });
                }}
              />
            ))}
            <p className="muted">Everybody gets the drop at these times, every drop day.</p>
          </>
        )}

        <label className="muted">Play window <span style={{ opacity: 0.7 }}>(how long members have to open a drop once it hits)</span></label>
        {/* Minutes, because the floor is five minutes and hours cannot say that.
            This page used to read the rounded-up hours column, so a 25 minute
            window came back as an hour and saving silently widened it. */}
        <div className="row" style={{ flexWrap: "wrap" }}>
          {(PLAY_WINDOW_MINUTES.includes(cfg.playWindowMinutes)
            ? PLAY_WINDOW_MINUTES
            : [...PLAY_WINDOW_MINUTES, cfg.playWindowMinutes].sort((a, b) => a - b)
          ).map((m) => (
            <button
              key={m}
              className={`chip ${cfg.playWindowMinutes === m ? "on" : ""}`}
              onClick={() => edit({ playWindowMinutes: m })}
            >
              {describeWindow(m)}
            </button>
          ))}
        </div>
        <p className="muted">Miss the window and you miss the drop, so give people enough room.</p>

        <label className="muted">Last call <span style={{ opacity: 0.7 }}>(a nudge before a drop closes)</span></label>
        <div className="seg" role="group" aria-label="Last call">
          <button className={cfg.lastCall ? "on" : ""} onClick={() => edit({ lastCall: true })}>Send it</button>
          <button className={!cfg.lastCall ? "on" : ""} onClick={() => edit({ lastCall: false })}>Skip it</button>
        </div>

        <label className="muted">Answer timer <span style={{ opacity: 0.7 }}>(seconds to answer EACH question after it appears)</span></label>
        <select className="input" value={cfg.answerSeconds} onChange={(e) => edit({ answerSeconds: Number(e.target.value) })}>
          {ANSWER_TIMERS.map((n) => <option key={n} value={n}>{n} seconds</option>)}
        </select>

        <label className="muted">Questions per drop <span style={{ opacity: 0.7 }}>(2 or 3 keeps a drop under a minute)</span></label>
        <input className="input" type="number" min={1} max={10} value={cfg.questionsPerDrop} onChange={(e) => edit({ questionsPerDrop: Number(e.target.value) })} />
        <p className="muted">
          A drop runs with whatever is approved and available, so asking for more than the crew has ready
          just means a shorter drop.
        </p>

        <label className="muted">Winning a week is worth <span style={{ opacity: 0.7 }}>(zero makes it one long race)</span></label>
        <input className="input" type="number" min={0} max={20} value={cfg.weekWinBonus} onChange={(e) => edit({ weekWinBonus: Number(e.target.value) })} />

        <label className="muted">Lifelines each <span style={{ opacity: 0.7 }}>(50/50s a member gets for the whole season)</span></label>
        <input className="input" type="number" min={0} max={10} value={cfg.lifelines} onChange={(e) => edit({ lifelines: Number(e.target.value) })} />

        {/* Right next to the button that refused, not in a banner at the top of
            a page this far down. */}
        {problems.length > 0 && (
          <div className="card live">
            <span className="tag">Not saved yet</span>
            {problems.map((p) => <p key={p} className="error">{p}</p>)}
          </div>
        )}

        <div className="row">
          <button className="btn ghost" disabled={busy} onClick={saveConfig}>{busy ? "Saving…" : "Save season settings"}</button>
          {season?.status === "draft" && <button className="btn primary" disabled={busy} onClick={activate}>Activate season</button>}
        </div>
        {season && (
          <p className="muted">
            {season.status === "draft"
              ? "This season is a draft. Activate it when the settings look right."
              : season.status === "active"
              ? "This season is running."
              : season.status === "paused"
              ? "This season is paused. No new drops will open until you resume."
              : "This season is finished."}
          </p>
        )}

        {season && season.status !== "draft" && (
          <>
            <p className="muted">
              Change anything above and hit <b>Save season settings</b>. If you changed the dates, the drop
              days or the time range, the upcoming drop times are rebuilt for you automatically.
            </p>
            <button className="btn ghost" disabled={rescheduling} onClick={reschedule}>
              {rescheduling ? "Shuffling…" : "Shuffle upcoming drop times"}
            </button>
            <p className="muted">
              Only needed if you want new random times without changing any settings. Drops that already
              happened are never touched.
            </p>
            {rescheduleMsg && <p className="muted">{rescheduleMsg}</p>}
          </>
        )}
      </div>

      {/* Wildcard / trivia */}
      <div className="card">
        <span className="tag">Wildcard days</span>
        <p className="muted">On a Wildcard day, the drop is general trivia instead of questions about your people. Pick none to keep every drop about your people.</p>
        <DayPicker selected={cfg.wildcardWeekdays} onToggle={(d) => edit({
          wildcardWeekdays: cfg.wildcardWeekdays.includes(d)
            ? cfg.wildcardWeekdays.filter((x) => x !== d)
            : [...cfg.wildcardWeekdays, d].sort(),
        })} />
        <label className="muted">Trivia theme (type "random" or a theme like "2006", "Southern hip-hop", "NBA")</label>
        <input className="input" value={cfg.wildcardTheme} onChange={(e) => edit({ wildcardTheme: e.target.value })} />
        <button className="btn ghost" disabled={busy} onClick={saveConfig}>Save these Wildcard days</button>
      </div>

        </>
      )}
      {sect === "crew" && (
        <>
      {/* What the crew is called, and where it lives. */}
      <div className="card">
        <span className="tag">Crew settings</span>
        <label className="muted">Crew name</label>
        <input className="input" value={lname} onChange={(e) => setLname(e.target.value)} />
        <label className="muted">Timezone <span style={{ opacity: 0.7 }}>(drop times follow this)</span></label>
        <select className="input" value={ltz} onChange={(e) => setLtz(e.target.value)}>
          {timeZoneOptions(ltz).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
        </select>
        {currentTimeIn(ltz) && <p className="muted">Right now it is {currentTimeIn(ltz)} there.</p>}
        <button className="btn ghost" disabled={busy} onClick={saveLeague}>Save crew settings</button>
      </div>

      <div className="card">
        <span className="tag">Crew link</span>
        <p className="muted">
          One link for everybody. Drop it in the group chat. It keeps working, so somebody
          who taps it next week still gets in.
        </p>
        {crewLink ? (
          <>
            <input className="input" readOnly value={crewLink} onFocus={(e) => e.target.select()} />
            <button className="btn primary" onClick={async () => {
              try { await navigator.clipboard.writeText(crewLink); setActionMsg("Copied. Paste it in the group chat."); }
              catch { setActionMsg("Copy it from the box above."); }
            }}>Copy the link</button>
            <div className="row">
              <button className="btn ghost" disabled={busy} onClick={() => void setLinkOpen(!crewLinkOpen)}>
                {crewLinkOpen ? "Stop new people joining" : "Let people join again"}
              </button>
              <button className="btn ghost" disabled={busy} onClick={() => {
                if (!window.confirm("Make a new link? The old one stops working for anyone who hasn't joined yet.")) return;
                void loadCrewLink(true);
              }}>
                New link
              </button>
            </div>
            <p className="muted" style={{ fontSize: 13 }}>
              {crewLinkOpen
                ? "Open. Anyone with the link can join."
                : "Closed. The link is still yours, it just won't let anybody new in until you reopen it."}
            </p>
          </>
        ) : (
          <button className="btn primary" disabled={busy} onClick={() => void loadCrewLink(false)}>
            Make the crew link
          </button>
        )}
      </div>

      {/* Invites — email-bound so joining never depends on which browser opens what */}
      <div className="card">
        <span className="tag">Invite members</span>
        <p className="muted">
          For one specific person. Most of the time the crew link above is easier.
        </p>
        <p className="muted">
          <b>Nothing is emailed to them.</b> You add the address here, then you send them the app yourself
          by text. When they sign in with that exact address, they join automatically.
        </p>
        <input
          className="input"
          type="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="name@email.com"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
        />
        <button className="btn accent" disabled={busy || !inviteEmail.includes("@")} onClick={makeInvite}>
          Add them to the crew
        </button>
        {invite && (
          <>
            <p>Added. Now text them. Either of these works:</p>
            <p className="muted">
              <b>Simplest:</b> send them <b>{appHost}</b> and tell them to sign in with the address you
              just entered.
            </p>
            <p className="muted"><b>Or</b> send them this personal link:</p>
            <input className="input" readOnly value={invite} onFocus={(e) => e.target.select()} />
            <button
              className="btn ghost"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(invite);
                  setActionMsg("Link copied. Paste it into a text.");
                } catch {
                  setActionMsg("Could not copy. Tap the box above and copy it by hand.");
                }
              }}
            >
              Copy link
            </button>
          </>
        )}
      </div>

      <div className="card">
        <span className="tag">Members</span>
        {status.members.filter((m: any) => m.inviteStatus !== "removed").map((m: any) => (
          <div className="member-row" key={m.userId}>
            <div className="lb-row" style={{ borderBottom: "none", paddingBottom: 0 }}>
              <span className="lb-name">{m.name}{m.role === "commissioner" ? " ⭐" : ""}
                <br /><span className="muted" style={{ fontSize: 12 }}>{m.email}</span>
                <br /><span className="muted" style={{ fontSize: 13 }}>
                  {m.checkedIn ? "checked in ✓" : "no check-in yet"}
                  {" · "}
                  {m.devices === 0
                    ? <span style={{ color: "var(--danger)" }}>no device registered</span>
                    : `${m.devices} device${m.devices === 1 ? "" : "s"}`}
                </span>
              </span>
              {/* Reports registered devices, which is what delivery now actually
                  uses. The old badge echoed push_status and had an "Email
                  fallback" state, which promised a channel that does not exist:
                  EMAIL_PROVIDER is mock, so those members were being told they
                  were covered while receiving nothing at all. */}
              <span className={`badge ${m.devices > 0 ? "ready" : "needs"}`}>
                {m.devices > 0 ? "Gets pinged" : "Gets nothing yet"}
              </span>
              <span className="lb-points">{m.readyCount}</span>
            </div>
            <div className="row" style={{ gap: 14, paddingBottom: 12 }}>
              <button className="link" onClick={() => op("resend", { targetUserId: m.userId })}>send ping</button>
              <button className="link" onClick={() => op("test-push", { targetUserId: m.userId })}>test</button>
              <button
                className="link"
                onClick={() => {
                  if (!window.confirm(`Reset notifications for ${m.name}? They will be asked to set them up again on whatever device they open next.`)) return;
                  op("reset-push", { targetUserId: m.userId });
                }}
              >
                reset notifications
              </button>
              {m.role !== "commissioner" && (
                <button
                  className="link"
                  onClick={() => {
                    if (!window.confirm(`Remove ${m.name} from the crew? Their questions stop being used and they lose access. Past results stay.`)) return;
                    op("remove-member", { targetUserId: m.userId });
                  }}
                >
                  remove
                </button>
              )}
            </div>
          </div>
        ))}
        <p className="muted">
          Numbers are approved-question counts. You cannot see anyone&apos;s question content.
          &quot;Send ping&quot; only works while a drop is live.
        </p>
        <p className="muted">
          <b>Gets nothing yet</b> means no phone is registered for that person, so a drop will not reach
          them at all. They have to open the app and turn notifications on themselves.
        </p>
      </div>

      {/* Invited but not signed in yet — the "who's still not in?" list. */}
      {(status.pendingInvites ?? []).length > 0 && (
        <div className="card">
          <span className="tag">Waiting on {status.pendingInvites.length} to sign in</span>
          {status.pendingInvites.map((p: any) => (
            <div className="lb-row" key={p.id}>
              <span className="lb-name muted" style={{ fontSize: 14 }}>{p.email}</span>
              <button
                className="link"
                onClick={() => {
                  if (!window.confirm(`Cancel the invite for ${p.email}?`)) return;
                  op("cancel-invite", { inviteId: p.id });
                }}
              >
                cancel
              </button>
            </div>
          ))}
          <p className="muted">
            They just install the app and sign in with that exact email and they'll join automatically.
            If someone here is already playing, they signed in with a different email. Check their address in
            the list above, then cancel this invite.
          </p>
        </div>
      )}

      {/* A drop with no approved questions is silently skipped, so surface the
          shortfall here BEFORE it costs a drop day. */}
      {totalReady < cfg.questionsPerDrop && season?.status !== "complete" && season?.status !== "paused" && (
        <div className="card live">
          <span className="tag">Not enough questions for the next drop</span>
          <p>
            The crew has <b>{totalReady}</b> approved question{totalReady === 1 ? "" : "s"} ready and you asked for
            <b> {cfg.questionsPerDrop}</b> per drop. A short drop still runs with whatever is available, so this is a
            heads up, not a blocker. Only a drop with <b>zero</b> questions gets skipped.
          </p>
          <p className="muted">Nudge the crew below for an update.</p>
        </div>
      )}

      {lowInventory.length > 0 && season?.status !== "complete" && (
        <div className="card">
          <span className="tag">Low inventory</span>
          <p className="muted">Total approved across the crew: {totalReady}</p>
          {lowInventory.map((m: any) => <p key={m.userId} className="muted">{m.name} has nothing ready yet.</p>)}
        </div>
      )}

        </>
      )}
      {sect === "drops" && (
        <>
      {/* Drop operations */}
      <div className="card">
        <span className="tag">Drops</span>
        <p className="muted">
          {status.dropCounts.scheduled} coming up · {status.dropCounts.live} live now · {status.dropCounts.revealed} played
          {status.dropCounts.cancelled > 0 ? ` · ${status.dropCounts.cancelled} cancelled` : ""}
        </p>
        <p className="muted">Exact future drop times are hidden. You're competing too.</p>
        <p className="muted">
          Opens a drop immediately and notifies everybody. It <b>ignores your drop time range</b>, so it
          works at any hour. Only two things stop it: a drop already being live, or nobody having an
          approved question.
        </p>
        <button
          className="btn ghost"
          onClick={() => {
            if (!window.confirm("Open a drop right now?\n\nEveryone gets notified and it counts for real, exactly like a scheduled drop.")) return;
            op("drop-now");
          }}
        >
          Start a drop now
        </button>
        <div className="row">
          {season?.status === "active" && <button className="btn ghost" onClick={() => op("pause")}>Pause future drops</button>}
          {season?.status === "paused" && <button className="btn ghost" onClick={() => op("resume")}>Resume</button>}
          {season?.status !== "complete" && (
            <button className="btn danger" onClick={() => {
              if (!window.confirm("End the season?\n\nNo more drops will open. You can reopen it afterwards if you end it by mistake.")) return;
              op("end");
            }}>End season</button>
          )}
          {season?.status === "complete" && (
            <button className="btn ghost" onClick={() => op("reopen")}>Reopen season</button>
          )}
        </div>
        {season?.status === "complete" && (
          // A host who ends the season had no way into the finale from here.
          // The one ping that links to it goes out at the moment they end it,
          // which is exactly when they are looking at this screen instead.
          <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/finale`)}>
            See the finale
          </button>
        )}
        <div className="row">
        </div>
      </div>
        </>
      )}
      {sect === "alerts" && (
        <>
      {/* Members */}
      <div className="card">
        <span className="tag">Notifications</span>
        <p className="muted">
          Send yourself a test to confirm pings actually reach your phone. Close the app first, or your
          phone may hold it silently while you are looking at the screen.
        </p>
        <button className="btn ghost" disabled={busy} onClick={() => op("test-push")}>
          Send me a test notification
        </button>
        <button className="btn ghost" disabled={busy} onClick={() => op("ping-all")}>
          Ping everyone about the live drop
        </button>
      </div>

        </>
      )}
    </div>
  );
}
